'use client';

import type React from 'react';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Loader2, Clock, Sun, Moon } from 'lucide-react';
import Image from 'next/image';
import 'flag-icons/css/flag-icons.min.css';
import { asianCountries } from '@/lib/countries';
import { toast } from 'sonner';
import { useSendOtp } from '@/hooks/useUsers';
import { useTheme } from '@/contexts/ThemeContext';

const SAVED_PHONE_COOKIE = 'saved_phone';
const COOKIE_DAYS = 30;

function savePhoneCookie(countryCode: string, phoneNumber: string) {
  const value = JSON.stringify({ countryCode, phoneNumber });
  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_DAYS);
  document.cookie = `${SAVED_PHONE_COOKIE}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

function getPhoneCookie(): { countryCode: string; phoneNumber: string } | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(^| )${SAVED_PHONE_COOKIE}=([^;]+)`));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match[2]));
  } catch {
    return null;
  }
}

function clearPhoneCookie() {
  document.cookie = `${SAVED_PHONE_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax`;
}

// The browser's autofill only remembers the national number, so the country
// code of every number used on this device is kept here to restore it.
const PHONE_COUNTRY_KEY = 'phone_country_codes';

function getPhoneCountryCodes(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(PHONE_COUNTRY_KEY) || '{}');
  } catch {
    return {};
  }
}

function savePhoneCountryCode(phoneNumber: string, countryCode: string) {
  try {
    const codes = getPhoneCountryCodes();
    codes[phoneNumber] = countryCode;
    localStorage.setItem(PHONE_COUNTRY_KEY, JSON.stringify(codes));
  } catch {}
}

export default function LoginPage() {
  const [countryCode, setCountryCode] = useState('+60');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [isSavedNumber, setIsSavedNumber] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const router = useRouter();
  const { mutate: mutateSendOtp, isPending } = useSendOtp();
  const { isDarkMode, toggleTheme } = useTheme();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Set by SessionExpiryWatcher when the token was revoked.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') !== '1') return;
    toast.info('Your session has ended. Please log in again.');
    window.history.replaceState(null, '', '/login');
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    const href = isDarkMode ? '/icon/icon-dark-192.png' : '/icon/icon-192.png';
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
  }, [isDarkMode, isMounted]);

  useEffect(() => {
    const saved = getPhoneCookie();
    if (saved?.phoneNumber) {
      const isValidCountry = asianCountries.some((c) => c.code === saved.countryCode);
      if (isValidCountry) {
        setCountryCode(saved.countryCode);
        setPhoneNumber(saved.phoneNumber);
        setIsSavedNumber(true);
      } else {
        // Stale/invalid cookie from before country list changed — clear it
        clearPhoneCookie();
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate phone number before submission
    const validation = validatePhoneNumber(phoneNumber, countryCode);
    if (!validation.isValid) {
      setPhoneError(validation.error);
      return;
    }

    const phone = `${countryCode}${phoneNumber}`;
    mutateSendOtp(
      { phoneNumber: phone },
      {
        onSuccess: (res: any) => {
          if (res?.message) toast.success(res.message);
          savePhoneCookie(countryCode, phoneNumber);
          savePhoneCountryCode(phoneNumber, countryCode);
          try {
            sessionStorage.setItem('phoneNumber', phone);
            sessionStorage.setItem('authFlow', 'login');
          } catch {}
          router.push('/verify-otp');
        },
        onError: (error: unknown) => {
          const msg =
            error instanceof Error ? error.message : 'Failed to send OTP';
          toast.error(msg);
        },
      },
    );
  };

  // Validation functions
  const validatePhoneNumber = (phone: string, countryCode: string) => {
    const country = asianCountries.find((c) => c.code === countryCode);
    if (!country) return { isValid: false, error: 'Country not supported' };

    if (!phone.trim())
      return { isValid: false, error: 'Phone number is required' };

    if (phone.length < country.minLength) {
      return {
        isValid: false,
        error: `Phone number must be at least ${country.minLength} digits for ${country.country}`,
      };
    }

    if (phone.length > country.maxLength) {
      return {
        isValid: false,
        error: `Phone number must be at most ${country.maxLength} digits for ${country.country}`,
      };
    }

    if (!country.pattern.test(phone)) {
      return {
        isValid: false,
        error: `Invalid phone number format for ${country.country}. Example: ${country.example}`,
      };
    }

    return { isValid: true, error: '' };
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setPhoneNumber(value);
    if (isSavedNumber) setIsSavedNumber(false);

    // A number picked from the browser's suggestions brings back its country code.
    const savedCountryCode = getPhoneCountryCodes()[value];
    const activeCountryCode =
      savedCountryCode && asianCountries.some((c) => c.code === savedCountryCode)
        ? savedCountryCode
        : countryCode;
    if (activeCountryCode !== countryCode) setCountryCode(activeCountryCode);

    // Clear error when user starts typing
    if (phoneError) setPhoneError('');

    // Real-time validation
    if (value.length > 0) {
      const validation = validatePhoneNumber(value, activeCountryCode);
      if (
        !validation.isValid &&
        value.length >=
          asianCountries.find((c) => c.code === activeCountryCode)?.minLength!
      ) {
        setPhoneError(validation.error);
      }
    }
  };

  const handleCountryChange = (newCountryCode: string) => {
    // Radix Select can fire onValueChange("") on its own when the page loads
    // with a prefilled value; ignore it so the saved country code is kept.
    if (!newCountryCode) return;
    setCountryCode(newCountryCode);
    setPhoneError('');
    if (isSavedNumber) setIsSavedNumber(false);

    // Re-validate phone number with new country
    if (phoneNumber) {
      const validation = validatePhoneNumber(phoneNumber, newCountryCode);
      if (!validation.isValid) {
        setPhoneError(validation.error);
      }
    }
  };

  const selectedCountry =
    asianCountries.find((country) => country.code === countryCode) ?? asianCountries[0];

  const getPlaceholderMask = (selectedCode: string) => {
    const country = asianCountries.find((c) => c.code === selectedCode);
    if (!country || !country.example) return '';
    const example = country.example.replace(/\D/g, '');
    if (example.length <= 2) return example.replace(/\d/g, 'X');
    const firstGroup = example.slice(0, 2).replace(/\d/g, 'X');
    const remaining = example.slice(2).replace(/\d/g, 'X');
    return `(${firstGroup}) ${remaining}`;
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-background transition-colors duration-200 relative">
      <button
        type="button"
        onClick={toggleTheme}
        title={isMounted && isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        className="absolute top-4 right-4 text-muted-foreground hover:text-primary transition-colors duration-300 cursor-pointer">
        {isMounted ? (
          isDarkMode ? <Sun className="h-6 w-6" /> : <Moon className="h-6 w-6" />
        ) : (
          <Moon className="h-6 w-6" />
        )}
      </button>
      <Card className="w-full max-w-md mx-auto border-0 shadow-none bg-card transition-colors duration-200">
        <CardHeader className="text-center space-y-6 pb-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-lg flex items-center justify-center p-2">
              <Image
                src={isMounted && isDarkMode ? '/sh_logo_dark.png' : '/sh_logo_light.png'}
                alt="Laundritek Logo"
                width={48}
                height={48}
                className="object-contain"
              />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            Laundritek
          </CardTitle>
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-foreground">
              Enter your phone Number
            </h2>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 border-b-2 border-b-primary focus-within:border-b-primary dark:rounded-t-lg dark:overflow-hidden">
                {/* Sized to its content so the country code is never clipped on narrow screens. */}
                <div className="shrink-0">
                  <Select
                    value={countryCode}
                    onValueChange={handleCountryChange}>
                    <SelectTrigger className="h-12! w-full border-0 rounded-none shadow-none focus-visible:ring-0 bg-white dark:[&_svg]:text-white">
                      <SelectValue>
                        <div className="flex items-center gap-2">
                          <span className={`fi fi-${selectedCountry.flag} text-lg`}></span>
                          <span className="text-foreground">{selectedCountry.code}</span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-60 bg-card border-border">
                      {asianCountries.map((country) => (
                        <SelectItem key={country.code} value={country.code} className="text-foreground">
                          <div className="flex items-center gap-2">
                            <span
                              className={`fi fi-${country.flag} text-lg`}></span>
                            <span className="text-foreground">
                              {country.code}
                            </span>
                            <span className="text-muted-foreground">
                              {country.country}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-0 relative">
                  <Input
                    type="tel"
                    name="phone"
                    autoComplete="login-phone"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    onFocus={() => setIsPhoneFocused(true)}
                    onBlur={() => setIsPhoneFocused(false)}
                    placeholder={
                      isPhoneFocused ? getPlaceholderMask(countryCode) : 'Phone No.'
                    }
                    className="h-12 border-0 rounded-none shadow-none focus:ring-0 focus-visible:ring-0 bg-white text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>
              </div>

              {phoneError && (
                <p className="text-sm text-red-600 dark:text-red-400 text-center">{phoneError}</p>
              )}

              {isSavedNumber && (
                <div className="flex items-center justify-between text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    <span>Using your last saved number</span>
                  </div>
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => {
                      setPhoneNumber('');
                      setCountryCode('+60');
                      setIsSavedNumber(false);
                      clearPhoneCookie();
                    }}>
                    Clear
                  </button>
                </div>
              )}

              <p className="text-sm text-muted-foreground text-center">
                An OTP will be sent to this number for verification
              </p>
            </div>

            <div className="space-y-4">
              {/* <div className="border-t border-gray-200"></div> */}

              <Button
                type="submit"
                className="w-full h-12 cursor-pointer bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg transition-colors duration-200"
                disabled={isPending || !phoneNumber.trim() || !!phoneError}>
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>

          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              By clicking on the Continue Button, you agree to our{' '}
              <span className="text-primary underline">Terms of use</span>.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
